/**
 * Localize FB onboarding step copy.
 *
 * steps.js is authored in English. Every other language lives in
 * ./locales/<lang>.json as a flat map keyed the SAME way the extraction script
 * keyed it, so this traversal and the dictionaries stay in lock-step:
 *
 *   "<stepKey>.<field>"          title | body | cta | question | sub | placeholder
 *   "<stepKey>.opt.<value>"      an option's label (keyed by option.value)
 *   "<stepKey>.hint.<value>"     an option's small hint line under the label
 *   "<stepKey>.arr.<i>"          a string in a string-array field (labels / areas)
 *   "<stepKey>.review.<field>"   nested testimonial (review / quote)
 *
 * A missing key falls back to the English source string, so a partial dictionary
 * can never blank out a screen. That is deliberate: de/no ship with the keys we
 * could not translate with confidence left OUT, so those lines read as honest
 * English instead of guessed German or Norwegian.
 * Unknown languages return the steps untouched.
 * `{name}` tokens are preserved verbatim here and substituted downstream by
 * withName(), exactly as for English. Some languages drop the token where their
 * grammar would need a case the funnel cannot produce (see the note in each
 * file); withName simply finds nothing to replace, which is safe.
 */
import es from './locales/es.json';
import fr from './locales/fr.json';
import lt from './locales/lt.json';
import ru from './locales/ru.json';
import de from './locales/de.json';
import nb from './locales/no.json';

const DICTS = { es, fr, lt, ru, de, no: nb };

// Same field sets the extractor used. Keep in sync if steps.js gains new copy.
const TEXT_FIELDS = new Set([
  'title', 'body', 'cta', 'question', 'sub', 'placeholder', 'text', 'author',
  'headline', 'subtitle', 'note', 'reveal', 'stat', 'source', 'label',
]);
const SKIP = new Set(['key', 'type', 'accent', 'image', 'photo', 'emoji', 'value', 'stars']);
const NESTED = new Set(['review', 'quote']);

function localizeStep(step, dict) {
  const k = step.key;
  const out = { ...step };
  for (const [field, val] of Object.entries(step)) {
    if (SKIP.has(field)) continue;

    if (typeof val === 'string' && TEXT_FIELDS.has(field)) {
      out[field] = dict[`${k}.${field}`] ?? val;
    } else if (NESTED.has(field) && val && typeof val === 'object') {
      const nested = { ...val };
      for (const [f2, v2] of Object.entries(val)) {
        if (typeof v2 === 'string' && TEXT_FIELDS.has(f2)) {
          nested[f2] = dict[`${k}.${field}.${f2}`] ?? v2;
        }
      }
      out[field] = nested;
    } else if (Array.isArray(val)) {
      out[field] = val.map((item, i) => {
        if (item && typeof item === 'object' && typeof item.label === 'string') {
          const id = item.value != null ? item.value : i;
          const next = { ...item, label: dict[`${k}.opt.${id}`] ?? item.label };
          // `hint` is the small grey line under an option label (SingleScreen /
          // VoiceScreen render it). It used to stay English in every locale.
          if (typeof item.hint === 'string') {
            next.hint = dict[`${k}.hint.${id}`] ?? item.hint;
          }
          return next;
        }
        if (typeof item === 'string') {
          return dict[`${k}.arr.${i}`] ?? item;
        }
        return item;
      });
    }
  }
  return out;
}

/**
 * Return a localized copy of a steps array for the given language.
 * For English (or any language without a dictionary) the original array is
 * returned by reference - zero overhead on the default funnel.
 */
export function localizeSteps(steps, lang) {
  const dict = DICTS[lang];
  if (!dict) return steps;
  return steps.map((s) => localizeStep(s, dict));
}
